import { useBillingPlan, useWallet } from "@features/billing";
import { useOrgs } from "@features/organisations";
import { formatCredits } from "@models/billing";
import BoltIcon from "@mui/icons-material/BoltRounded";
import HourglassIcon from "@mui/icons-material/HourglassEmptyRounded";
import { Box, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useAuthStore } from "@stores/authStore";
import { useOrgStore } from "@stores/orgStore";
import { useNavigate } from "@tanstack/react-router";

/**
 * Sidebar credits pill — replaces the dollar-spend pill with the active
 * scope's wallet balance.
 *
 * Personal context → this user's personal wallet.
 * Org context      → the organisation's shared wallet.
 *
 * Click → /settings/billing for the active scope. The button shows three
 * meaningful states: trial (clock overlay + amber tone), low (<10% of a
 * reasonable cycle, amber dot), exhausted (red dot). Loading and error
 * fall back to a discreet em-dash so the chrome never goes blank.
 */
export function SidebarCredits() {
  const user = useAuthStore((s) => s.user);
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const { data: orgs } = useOrgs();
  const navigate = useNavigate();
  const { data: wallet, isLoading, isError } = useWallet();
  const { data: plan } = useBillingPlan();

  const scopeLabel = activeOrgId
    ? (orgs?.find((o) => o.id === activeOrgId)?.name ?? "Organisation")
    : "Personal";

  if (!user) return null;

  const balance = wallet?.balance ?? null;
  const isTrial = plan?.id === "trial";

  // "Low" is anything under 200 credits — calibrated against a Pro cycle
  // (2,500 credits/mo) so the warning shows up before the user is stuck
  // mid-conversation, not after.
  const lowThreshold = 200;
  const isLow = balance != null && balance > 0 && balance < lowThreshold;
  const isExhausted = balance === 0;

  const dotColor = isExhausted
    ? "error.main"
    : isLow
      ? "warning.main"
      : isTrial
        ? "warning.main"
        : null;

  const label = isLoading
    ? "—"
    : isError
      ? "—"
      : balance == null
        ? "—"
        : formatCredits(balance);

  const tooltipBody = (() => {
    if (isError) return "Couldn't load credit balance.";
    if (balance == null) return "Loading credit balance…";
    if (isExhausted) return `Out of credits in ${scopeLabel}. Click to top up.`;
    if (isTrial) {
      return `${formatCredits(balance)} trial credits in ${scopeLabel}. Click to upgrade.`;
    }
    if (isLow) {
      return `Only ${formatCredits(balance)} credits left in ${scopeLabel}. Click to top up.`;
    }
    return `${formatCredits(balance)} credits in ${scopeLabel}. Click to manage.`;
  })();

  return (
    <Tooltip title={tooltipBody} placement="right">
      <Box
        component="button"
        onClick={() => void navigate({ to: "/settings/billing" })}
        aria-label={`${formatCredits(balance)} credits in ${scopeLabel}. Open billing.`}
        sx={(t) => {
          // Pick the rim tone — error / warning / trace-cyan — so the
          // pill picks up the same accent color whether things are
          // healthy, low, or exhausted.
          const accentTone = isExhausted
            ? t.palette.error.main
            : isLow || isTrial
              ? t.palette.warning.main
              : t.palette.primary.main;
          return {
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1,
            py: 0.75,
            borderRadius: 1,
            bgcolor: "surface.raised",
            border: "1px solid",
            borderColor: isExhausted || isLow || isTrial
              ? accentTone
              : t.palette.border.default,
            cursor: "pointer",
            textAlign: "left",
            width: "100%",
            font: "inherit",
            color: "inherit",
            position: "relative",
            transition: `border-color ${t.motion.fast}, background-color ${t.motion.fast}, box-shadow ${t.motion.fast}`,
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              borderRadius: "inherit",
              boxShadow: `inset 0 0 0 1px ${alpha(accentTone, isExhausted || isLow || isTrial ? 0.18 : 0.05)}`,
              transition: `box-shadow ${t.motion.fast}`,
            },
            "&:hover, &:focus-visible": {
              borderColor: accentTone,
              bgcolor: alpha(accentTone, 0.05),
            },
            "&:hover::before, &:focus-visible::before": {
              boxShadow: `inset 0 0 0 1px ${alpha(accentTone, 0.25)}`,
            },
            "&:focus-visible": { outline: "none" },
          };
        }}
      >
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <BoltIcon
            sx={{
              fontSize: 18,
              color: isExhausted
                ? "error.main"
                : isLow
                  ? "warning.main"
                  : isTrial
                    ? "warning.main"
                    : "primary.main",
            }}
          />
          {isTrial && (
            <HourglassIcon
              sx={{
                position: "absolute",
                bottom: -3,
                right: -5,
                fontSize: 10,
                color: "warning.main",
                bgcolor: "surface.raised",
                borderRadius: "50%",
                p: "1px",
              }}
            />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontFamily: (t) => t.fonts.mono,
              color: "text.secondary",
              fontSize: "0.625rem",
              lineHeight: 1.1,
              textTransform: "uppercase",
              letterSpacing: "0.18em",
              fontWeight: 500,
            }}
          >
            {isTrial ? "Trial" : "Credits"} · {scopeLabel}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.95rem",
              fontWeight: 600,
              color: "text.primary",
              lineHeight: 1.2,
            }}
          >
            {label}
          </Typography>
        </Box>
        {dotColor && (
          <Box
            aria-hidden
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: dotColor,
              flexShrink: 0,
              boxShadow: (t) => `0 0 0 2px ${t.palette.surface.raised}`,
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
}
